import { useMemo, useState } from "react";
import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { adminBlogPostsQuery } from "@/lib/blog/queries";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";

export const Route = createFileRoute("/admin/blog")({ component: BlogAdminLayout });

function BlogAdminLayout() {
  const matches = useMatches();
  const hasChild = matches.some((m) => m.routeId === "/admin/blog/$id");
  if (hasChild) return <Outlet />;
  return <BlogAdminIndex />;
}

function BlogAdminIndex() {
  const { data, isLoading } = useQuery(adminBlogPostsQuery());
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const posts = useMemo(() => {
    const list = data?.posts ?? [];
    const term = q.trim().toLowerCase();
    return list.filter(
      (p) =>
        (status === "all" || p.status === status) &&
        (!term || p.title.toLowerCase().includes(term) || p.slug.includes(term)),
    );
  }, [data, q, status]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Blog posts"
        subtitle={`${data?.posts.length ?? 0} articles | rich editor, SEO and scheduling`}
        action={
          <Link
            to="/admin/blog/$id"
            params={{ id: "new" }}
            className="border border-black/20 bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white"
          >
            New post
          </Link>
        }
      />
      <div className="flex flex-wrap gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title or slug"
          className="h-9 min-w-[220px] flex-1 border border-black/15 px-3 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 border border-black/15 px-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="hidden">Hidden</option>
          <option value="archived">Archived</option>
        </select>
        <Link
          to="/admin/blog-taxonomy"
          className="flex h-9 items-center border border-black/15 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-black/70 hover:border-black/50"
        >
          Categories · Tags · Authors
        </Link>
      </div>
      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Author</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Published</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td className="px-3 py-6 text-black/50" colSpan={6}>Loading…</td></tr>
            )}
            {!isLoading && posts.length === 0 && (
              <tr><td className="px-3 py-6 text-black/50" colSpan={6}>No posts found.</td></tr>
            )}
            {posts.map((p) => (
              <tr key={p.id} className="border-b border-black/5">
                <td className="px-3 py-2 font-medium text-black">
                  {p.title}
                  {p.isFeatured && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#C6A15B]">featured</span>}
                  <div className="font-mono text-[11px] text-black/45">/blog/{p.slug}</div>
                </td>
                <td className="px-3 py-2 text-black/70">{p.categoryName}</td>
                <td className="px-3 py-2 text-black/70">{p.authorName}</td>
                <td className="px-3 py-2">
                  {p.status === "published" ? <StatusPill tone="success">Published</StatusPill>
                    : p.status === "draft" ? <StatusPill tone="warn">Draft</StatusPill>
                    : p.status === "hidden" ? <StatusPill tone="info">Hidden</StatusPill>
                    : <StatusPill>Archived</StatusPill>}
                </td>
                <td className="px-3 py-2 text-black/70">
                  {p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Link
                    to="/admin/blog/$id"
                    params={{ id: p.id }}
                    className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}